import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { catalogApiRef, useAsyncEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import type { DataTableColumn } from '@mercedes-benz/odp-theme-v2';
import { DataTable } from '@mercedes-benz/odp-theme-v2';
import { OwnerLink } from '../components/OwnerLink';
import Button from '@mui/material/Button';

const DEFAULT_PAGE_SIZE = 50;

interface RelatedEntitiesTabProps {
  /** Backstage relation type constant, e.g. RELATION_HAS_PART */
  relationType: string;
  /** Filter targets by kind (case-insensitive). Omit to include all kinds. */
  kindFilter?: string;
  title: string;
}

export const RelatedEntitiesTab = ({
  relationType,
  kindFilter,
  title,
}: Readonly<RelatedEntitiesTabProps>) => {
  const { entity, loading: entityLoading } = useAsyncEntity();
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const [allRefs, setAllRefs] = useState<string[]>([]);
  const [relatedEntities, setRelatedEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    if (entityLoading) return;
    if (!entity) {
      setAllRefs([]);
      setRelatedEntities([]);
      setLoading(false);
      return;
    }

    const relations = (entity.relations ?? []).filter(r => {
      if (r.type !== relationType) return false;
      if (kindFilter) {
        return (
          parseEntityRef(r.targetRef).kind.toLowerCase() ===
          kindFilter.toLowerCase()
        );
      }
      return true;
    });

    setAllRefs(relations.map(r => r.targetRef));
    setPage(0);
  }, [entity, entityLoading, relationType, kindFilter]);

  const fetchPage = useCallback(
    async (refs: string[], pageNum: number, size: number) => {
      const start = pageNum * size;
      const end = start + size;
      const pageRefs = refs.slice(start, end);

      if (pageRefs.length === 0) {
        setRelatedEntities([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await catalogApi.getEntitiesByRefs({
          entityRefs: pageRefs,
        });
        setRelatedEntities(
          result.items.filter((e): e is Entity => e !== undefined),
        );
      } catch {
        setRelatedEntities([]);
      } finally {
        setLoading(false);
      }
    },
    [catalogApi],
  );

  useEffect(() => {
    fetchPage(allRefs, page, pageSize);
  }, [allRefs, page, pageSize, fetchPage]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(0);
  }, []);

  const columns: DataTableColumn[] = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (row: any) => (
        <Button
          role="button"
          tabIndex={0}
          style={{ color: '#4A9EFF', cursor: 'pointer', fontWeight: 500 }}
          onClick={() =>
            navigate(`/catalog/${row.namespace}/${row.kind}/${row.name}`)
          }
          onKeyDown={e =>
            e.key === 'Enter' &&
            navigate(`/catalog/${row.namespace}/${row.kind}/${row.name}`)
          }
        >
          {row.name}
        </Button>
      ),
    },
    { id: 'kind', label: 'Kind', sortable: true },
    {
      id: 'owner',
      label: 'Owner',
      sortable: true,
      render: (row: any) => <OwnerLink owner={row.owner} />,
    },
    { id: 'type', label: 'Type', sortable: true },
    { id: 'lifecycle', label: 'Lifecycle', sortable: true },
    { id: 'description', label: 'Description', sortable: false },
  ];

  const data = relatedEntities.map(e => ({
    name: e.metadata.name,
    namespace: e.metadata.namespace ?? 'default',
    kind: e.kind.toLowerCase(),
    owner: (e.spec?.owner as string) ?? '',
    type: (e.spec?.type as string) ?? '',
    lifecycle: (e.spec?.lifecycle as string) ?? '',
    description: e.metadata.description ?? '',
  }));

  if (entityLoading || loading) {
    return (
      <div
        style={{
          padding: 48,
          textAlign: 'center',
          color: 'rgba(255,255,255,0.55)',
        }}
      >
        Loading…
      </div>
    );
  }

  return (
    <DataTable
      title={title}
      columns={columns}
      data={data}
      searchable
      serverSide={{
        totalCount: allRefs.length,
        page,
        pageSize,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
      }}
    />
  );
};
